import React, { useState, useEffect } from "react";
import { FaCloud } from "react-icons/fa";

// Array of funny reload messages (outside component to prevent re-creation)
const funnyMessages = [
  "Waking up the website from its nap...",
  "Giving the pixels a good stretch...",
  "Brewing fresh content just for you...",
  "Shaking off the digital dust...",
  "Convincing the code to cooperate...",
  "Polishing the virtual doorknob...",
  "Teaching the website new tricks...",
  "Untangling the web of possibilities..."
];

export default function ReloadIndicator() {
  const [showReloadScreen, setShowReloadScreen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentMessage, setCurrentMessage] = useState("");

  useEffect(() => {
    // Check if this is a page reload
    const wasReloaded = sessionStorage.getItem('pageReloaded');
    
    if (wasReloaded === 'true') {
      // Pick a random funny message
      const randomMessage = funnyMessages[Math.floor(Math.random() * funnyMessages.length)];
      setCurrentMessage(randomMessage);
      
      setShowReloadScreen(true);
      setIsAnimating(true);
      
      // Remove the reload flag after showing the screen
      sessionStorage.removeItem('pageReloaded');
      
      // Hide the screen after 4 seconds
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setTimeout(() => {
          setShowReloadScreen(false);
        }, 500); // Wait for fade out animation
      }, 4000);
      
      return () => clearTimeout(timer);
    }

    // Set up beforeunload listener to detect page reloads
    const handleBeforeUnload = () => {
      sessionStorage.setItem('pageReloaded', 'true');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  if (!showReloadScreen) return null;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black transition-all duration-500 ${
      isAnimating ? 'opacity-100' : 'opacity-0'
    }`}>
      {/* Apple-style gradient background layers - same as home page */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800"></div>
      <div className="absolute inset-0 bg-gradient-to-tl from-gray-700/20 via-transparent to-gray-600/30"></div>
      <div className="absolute inset-0 bg-gradient-radial from-gray-600/10 via-transparent to-transparent"></div>
      
      {/* Blur overlay for Apple effect */}
      <div className="absolute inset-0 backdrop-blur-3xl bg-black/30"></div>
      
      {/* Content */}
      <div className="relative z-10 text-center">
        {/* Circular loading wheel with cloud icon */}
        <div className="relative inline-block mb-8">
          {/* Outer spinning ring */}
          <div className={`w-32 h-32 border-4 border-white/20 border-t-white rounded-full ${
            isAnimating ? 'animate-spin' : ''
          }`}></div>
          
          {/* Inner cloud icon */}
          <div className="absolute inset-0 flex items-center justify-center">
            <FaCloud className="text-white text-4xl" />
          </div>
        </div>
        
        {/* Funny message */}
        <div className={`transition-all duration-300 delay-200 ${
          isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <p className="text-xl text-white font-medium mb-2">
            {currentMessage}
          </p>
          <p className="text-sm text-gray-300">
            Just a moment while we freshen things up!
          </p>
        </div>
        
        {/* Optional: Small dots animation */}
        <div className={`flex justify-center mt-6 space-x-1 ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}>
          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce"></div>
          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
        </div>
      </div>
    </div>
  );
}
